const getApiBase = () => {
  const configured = process.env.REACT_APP_API_BASE;
  if (!configured) return "";
  return configured.replace(/\/v1\/api\/?$/, "").replace(/\/$/, "");
};

const UNAVAILABLE_COOLDOWN_MS = 60000;
let unavailableUntil = 0;

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

const request = async (path, options = {}, { ignoreUnavailableCooldown = false } = {}) => {
  if (!ignoreUnavailableCooldown && Date.now() < unavailableUntil) {
    throw new Error("Servicio temporalmente no disponible");
  }

  let response;
  try {
    response = await fetch(`${getApiBase()}${path}`, {
      ...options,
      headers: {
        ...authHeaders(),
        ...(options.headers || {}),
      },
    });
  } catch (err) {
    unavailableUntil = Date.now() + UNAVAILABLE_COOLDOWN_MS;
    throw err;
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    if ([502, 503, 504].includes(response.status)) {
      unavailableUntil = Date.now() + UNAVAILABLE_COOLDOWN_MS;
    }
    throw new Error(data.message || "No se pudo completar la operacion");
  }
  unavailableUntil = 0;
  return data;
};

const downloadRequest = async (path) => {
  const response = await fetch(`${getApiBase()}${path}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || "No se pudo descargar el archivo");
  }
  return response.blob();
};

const withQuery = (path, params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") query.set(key, value);
  });
  const qs = query.toString();
  return qs ? `${path}?${qs}` : path;
};

const asList = (payload, keys = []) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  for (const key of [...keys, "data", "rows", "items"]) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  return [];
};

const asCameraPayload = (payload) => {
  if (Array.isArray(payload)) return { imported: 0, cameras: payload };
  return {
    ...(payload && typeof payload === "object" ? payload : {}),
    cameras: asList(payload, ["cameras"]),
  };
};

const asRealtimeAlertsPayload = (payload) => {
  if (Array.isArray(payload)) return { alerts: payload, unseen_count: 0 };
  return {
    ...(payload && typeof payload === "object" ? payload : {}),
    alerts: asList(payload, ["alerts"]),
    unseen_count: Number(payload?.unseen_count || 0),
  };
};

const asPaged = (payload) => ({
  data: asList(payload, ["data"]),
  pagination: payload?.pagination || { page: 1, limit: 50, total: asList(payload, ["data"]).length, total_pages: 1 },
});

export const saasApi = {
  getEdgeArtifact: () => request('/api/saas/edge/artifact'),
  getEdgeInstallations: (params) => request(withQuery('/api/saas/edge/installations', params)),
  createEdgePairing: (body) => request('/api/saas/edge/pairings', { method: 'POST', body: JSON.stringify(body) }),
  revokeEdge: (id) => request(`/api/saas/edge/installations/${encodeURIComponent(id)}/revoke`, { method: 'POST' }),
  discoverEdge: (id) => request(`/api/saas/edge/installations/${encodeURIComponent(id)}/discover`, { method: 'POST' }),
  getEdgeCommand: (id) => request(`/api/saas/edge/commands/${encodeURIComponent(id)}`),
  registerEdgeCamera: (id, body) => request(`/api/saas/edge/commands/${encodeURIComponent(id)}/register-camera`, { method: 'POST', body: JSON.stringify(body) }),
  getOnboardingAccess: () => request('/api/onboarding/access'),
  setSuperadminOnboardingAccess: (id, payload) => request(`/api/saas/superadmin/clients/${id}/onboarding-access`, { method: 'PUT', body: JSON.stringify(payload) }),
  getPermissions: () => request("/api/saas/permissions"),
  getBootstrap: (params) => request(withQuery("/api/saas/bootstrap", params)),
  getDashboard: () => request("/api/saas/dashboard"),
  getExecutiveSummary: (params) => request(withQuery("/api/saas/dashboard/executive", params)),
  getCommercialAnalytics: (params) => request(withQuery("/api/saas/commercial-analytics", params)),
  getDetectionAnalytics: (params) => request(withQuery("/api/saas/analytics/detections", params)),
  getVideoWall: (params) => request(withQuery("/api/saas/video-wall", params)),
  getReportConfig: () => request("/api/saas/reports/config"),
  saveReportConfig: (payload) => request("/api/saas/reports/config", {
    method: "PUT",
    body: JSON.stringify(payload),
  }),
  getMonthlyReport: (params) => request(withQuery("/api/saas/reports/monthly", params)),
  getSuperadminOverview: () => request("/api/saas/superadmin/overview"),
  getSuperadminClients: (params) => request(withQuery("/api/saas/superadmin/clients", params)).then(asPaged),
  getSuperadminClient: (id) => request(`/api/saas/superadmin/clients/${id}`),
  createSuperadminManualCoverage: (id, payload) => request(`/api/saas/superadmin/clients/${id}/billing/coverage`, { method: "POST", body: JSON.stringify(payload) }),
  getSuperadminBillingCredentials: (id) => request(`/api/saas/superadmin/clients/${id}/billing/credentials`),
  createSuperadminBillingCredential: (id, payload) => request(`/api/saas/superadmin/clients/${id}/billing/credentials`, { method: "POST", body: JSON.stringify(payload) }),
  revokeSuperadminBillingCredential: (organizationId, credentialId) => request(`/api/saas/superadmin/clients/${organizationId}/billing/credentials/${credentialId}`, { method: "DELETE" }),
  setSuperadminBillingSubscriptionStatus: (id, payload) => request(`/api/saas/superadmin/clients/${id}/billing/subscription/status`, { method: "POST", body: JSON.stringify(payload) }),
  reconcileSuperadminBilling: (id) => request(`/api/saas/superadmin/clients/${id}/billing/reconcile`, { method: "POST", body: JSON.stringify({}) }),
  getSuperadminClientUsers: (id, params) => request(withQuery(`/api/saas/superadmin/clients/${id}/users`, params)).then(asPaged),
  getSuperadminClientCameras: (id, params) => request(withQuery(`/api/saas/superadmin/clients/${id}/cameras`, params)).then(asPaged),
  getSuperadminClientDetections: (id, params) => request(withQuery(`/api/saas/superadmin/clients/${id}/detections`, params)).then(asPaged),
  getSuperadminClientAlerts: (id, params) => request(withQuery(`/api/saas/superadmin/clients/${id}/alerts`, params)).then(asPaged),
  getSuperadminCameras: (params) => request(withQuery("/api/saas/superadmin/cameras", params)).then(asPaged),
  getSuperadminDetections: (params) => request(withQuery("/api/saas/superadmin/detections", params)).then(asPaged),
  getSuperadminAlerts: (params) => request(withQuery("/api/saas/superadmin/alerts", params)).then(asPaged),
  getSuperadminAudit: (params) => request(withQuery("/api/saas/superadmin/audit", params)).then(asPaged),
  getSuperadminHealth: () => request("/api/saas/superadmin/health"),
  getSuperadminBillingCatalog: () => request("/api/saas/superadmin/billing/catalog"),
  previewSuperadminBillingCatalog: (id, params) => request(withQuery(`/api/saas/superadmin/billing/catalog/${id}/preview`, params)),
  createSuperadminBillingCatalogDraft: (payload) => request("/api/saas/superadmin/billing/catalog/drafts", {
    method: "POST",
    body: JSON.stringify(payload),
  }),
  publishSuperadminBillingCatalog: (id, payload) => request(`/api/saas/superadmin/billing/catalog/${id}/publish`, {
    method: "POST",
    body: JSON.stringify(payload),
  }),
  getBillingCatalog: () => request("/api/saas/billing/catalog"),
  getBillingSetupCameras: () => request("/api/saas/billing/setup-cameras"),
  getBillingState: () => request("/api/saas/billing/state"),
  quoteBillingSelection: (payload) => request("/api/saas/billing/quote", {
    method: "POST",
    body: JSON.stringify(payload),
  }),
  createBillingChangeRequest: (payload) => request("/api/saas/billing/change-requests", {
    method: "POST",
    body: JSON.stringify(payload),
  }),
  startBillingSubscription: () => request("/api/saas/billing/subscription", { method: "POST", body: JSON.stringify({}) }),
  reviewSuperadminBillingRequest: (id, payload) => request(`/api/saas/superadmin/billing/change-requests/${id}/review`, { method: "POST", body: JSON.stringify(payload) }),
  startImpersonation: (payload) => request("/api/saas/superadmin/impersonations", {
    method: "POST",
    body: JSON.stringify(payload),
  }),
  endImpersonation: (payload) => request("/api/saas/superadmin/impersonations/end", {
    method: "POST",
    body: JSON.stringify(payload || {}),
  }),
  getSupportConversations: (params) => request(withQuery("/api/saas/support/conversations", params)).then(asPaged),
  getSupportKnowledgeCategories: () => request("/api/saas/support/knowledge/categories").then((payload) => asList(payload, ["data"])),
  searchSupportKnowledge: (payload) => request("/api/saas/support/knowledge/search", { method: "POST", body: JSON.stringify(payload) }).then((data) => asList(data, ["data"])),
  createSupportConversation: (payload) => request("/api/saas/support/conversations", {
    method: "POST",
    body: JSON.stringify(payload),
  }),
  getSupportMessages: (id, params) => request(withQuery(`/api/saas/support/conversations/${id}/messages`, params)).then(asPaged),
  sendSupportMessage: (id, payload) => request(`/api/saas/support/conversations/${id}/messages`, {
    method: "POST",
    body: JSON.stringify(payload),
  }),
  updateSupportConversation: (id, payload) => request(`/api/saas/support/conversations/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  }),
  getSuperadminSupportConversations: (params) => request(withQuery("/api/saas/superadmin/support/conversations", params)).then(asPaged),
  getSuperadminSupportKnowledge: (params) => request(withQuery("/api/saas/superadmin/support/knowledge", params)).then(asPaged),
  createSuperadminSupportKnowledge: (payload) => request("/api/saas/superadmin/support/knowledge", { method: "POST", body: JSON.stringify(payload) }),
  updateSuperadminSupportKnowledge: (id, payload) => request(`/api/saas/superadmin/support/knowledge/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
  importSuperadminSupportKnowledge: (articles) => request("/api/saas/superadmin/support/knowledge/import", { method: "POST", body: JSON.stringify({ articles }) }),
  deleteSuperadminSupportKnowledge: (id) => request(`/api/saas/superadmin/support/knowledge/${id}`, { method: "DELETE" }),
  getSuperadminSupportConversation: (id) => request(`/api/saas/superadmin/support/conversations/${id}`),
  getSuperadminSupportMessages: (id, params) => request(withQuery(`/api/saas/superadmin/support/conversations/${id}/messages`, params)).then(asPaged),
  updateSuperadminSupportConversation: (id, payload) => request(`/api/saas/superadmin/support/conversations/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  }),
  sendSuperadminSupportMessage: (id, payload) => request(`/api/saas/superadmin/support/conversations/${id}/messages`, {
    method: "POST",
    body: JSON.stringify(payload),
  }),
  getOrganizations: () => request("/api/saas/organizations").then((data) => asList(data, ["organizations"])),
  createOrganization: (payload) => request("/api/saas/organizations", {
    method: "POST",
    body: JSON.stringify(payload),
  }),
  getBranches: (params) => request(withQuery("/api/saas/branches", params)).then((data) => asList(data, ["branches"])),
  createBranch: (payload) => request("/api/saas/branches", {
    method: "POST",
    body: JSON.stringify(payload),
  }),
  updateBranch: (id, payload) => request(`/api/saas/branches/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  }),
  archiveBranch: (id) => request(`/api/saas/branches/${id}`, {
    method: "DELETE",
  }),
  getUsers: (params) => request(withQuery("/api/saas/users", params)).then((data) => asList(data, ["users"])),
  createUser: (payload) => request("/api/saas/users", {
    method: "POST",
    body: JSON.stringify(payload),
  }),
  updateUser: (id, payload) => request(`/api/saas/users/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  }),
  updateUserStatus: (id, payload) => request(`/api/saas/users/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  }),
  deleteUser: (id) => request(`/api/saas/users/${id}`, {
    method: "DELETE",
  }),
  getCameras: (params) => request(withQuery("/api/saas/cameras", params)).then(asCameraPayload),
  createCamera: (payload) => request("/api/saas/cameras", {
    method: "POST",
    body: JSON.stringify(payload),
  }),
  testCameraConnection: (payload) => request("/api/saas/cameras/test", {
    method: "POST",
    body: JSON.stringify(payload),
  }),
  updateCamera: (id, payload) => request(`/api/saas/cameras/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  }),
  archiveCamera: (id) => request(`/api/saas/cameras/${id}`, {
    method: "DELETE",
  }),
  // Tabla legacy `tenant_cameras`: es la que alimenta "Video AI Procesado" en el
  // dashboard. Se mantiene espejada con la tabla nueva hasta que esa seccion migre.
  getLegacyCameras: () => request("/api/cameras").then((data) => asList(data, ["cameras"])),
  testLegacyCameraConnection: (payload) => request('/api/cameras/test', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
  createLegacyCamera: (payload) => request("/api/cameras", {
    method: "POST",
    body: JSON.stringify(payload),
  }),
  archiveLegacyCamera: (id) => request(`/api/cameras/${id}`, {
    method: "DELETE",
  }),
  getAlerts: (params) => request(withQuery("/api/saas/alerts", params)).then((data) => asList(data, ["alerts"])),
  getIncidents: (params) => request(withQuery('/api/saas/incidents', params)),
  getIncidentTimeline: (id) => request(`/api/saas/alerts/${id}/timeline`),
  exportIncidents: (params) => downloadRequest(withQuery('/api/saas/incidents/export', params)),
  getAlert: (id) => request(`/api/saas/alerts/${id}`),
  getRealtimeAlerts: () => request("/api/saas/alerts/realtime", {}, { ignoreUnavailableCooldown: true }).then(asRealtimeAlertsPayload),
  updateAlert: (id, payload) => request(`/api/saas/alerts/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  }),
  acknowledgeAlert: (id) => request(`/api/saas/alerts/${id}/ack`, { method: 'POST' }),
  addAlertComment: (id, payload) => request(`/api/saas/alerts/${id}/comments`, {
    method: "POST",
    body: JSON.stringify(payload),
  }),
  markAlertViewed: (id) => request(`/api/saas/alerts/${id}/view`, {
    method: "POST",
  }),
  getClips: (params) => request(withQuery("/api/saas/clips", params)).then((data) => asList(data, ["clips"])),
  createClipLink: (id, payload) => request(`/api/saas/clips/${id}/link`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
  }),
  downloadClip: (id) => downloadRequest(`/api/saas/clips/${id}/download`),
  getDetectionConfig: (params) => request(withQuery("/api/saas/detections-config", params)),
  saveDetectionConfig: (payload) => request("/api/saas/detections-config", {
    method: "POST",
    body: JSON.stringify(payload),
  }),
  getDetectionConfigHistory: (params) => request(withQuery("/api/saas/detections-config/history", params)).then((data) => asList(data, ["history", "configs"])),
  rollbackDetectionConfig: (id) => request(`/api/saas/detections-config/${id}/rollback`, {
    method: "POST",
  }),
  markDetectionConfigApplied: (id) => request(`/api/saas/detections-config/${id}/applied`, {
    method: "POST",
  }),
  disableDetectionConfig: (id) => request(`/api/saas/detections-config/${id}`, {
    method: "DELETE",
  }),
  getInternalDetectionsConfig: () => request("/api/saas/internal-config/detections").then((data) => data.configuration),
  saveInternalDetectionsConfig: (configuration) => request("/api/saas/internal-config/detections", {
    method: "PUT",
    body: JSON.stringify({ configuration }),
  }).then((data) => {
    if (data?.ok !== true || !data.configuration) throw new Error("Respuesta invalida al guardar detecciones");
    return data.configuration;
  }),
  getInternalCamerasConfig: () => request("/api/saas/internal-config/cameras").then((data) => data.configuration),
  saveInternalCamerasConfig: (configuration) => request("/api/saas/internal-config/cameras", {
    method: "PUT",
    body: JSON.stringify({ configuration }),
  }).then((data) => {
    if (data?.ok !== true || !data.configuration) throw new Error("Respuesta invalida al guardar camaras");
    return data.configuration;
  }),
  getAiInstances: (params) => request(withQuery("/api/saas/ai-instances", params)).then((data) => asList(data, ["instances", "aiInstances"])),
  createAiInstance: (payload) => request("/api/saas/ai-instances", {
    method: "POST",
    body: JSON.stringify(payload),
  }),
  updateAiInstance: (id, payload) => request(`/api/saas/ai-instances/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  }),
  updateAiSchedule: (id, payload) => request(`/api/saas/ai-instances/${id}/schedule`, {
    method: "PUT",
    body: JSON.stringify(payload),
  }),
  getHealth: () => request("/api/saas/health"),
  getNotificationContacts: () => request("/api/notification-contacts").then((data) => asList(data, ["contacts"])),
  createNotificationContact: (payload) => request("/api/notification-contacts", {
    method: "POST",
    body: JSON.stringify(payload),
  }),
  getOnboarding: (params) => request(withQuery("/api/saas/onboarding", params)),
  runOnboardingFinalTest: (payload) => request("/api/saas/onboarding/final-test", {
    method: "POST",
    body: JSON.stringify(payload || {}),
  }),
  getAuditLogs: (params) => request(withQuery("/api/saas/audit-logs", params)).then((data) => asList(data, ["logs", "auditLogs"])),
};

export default saasApi;
